import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  confirmMatchRegistration,
  createLeaderboardBannerUploadUrl,
  clearCompetitionOutcome,
  createCompetitionOutcome,
  createOwnerManagedMatchRegistration,
  getMatch,
  listMatchLeaderboards,
  listCompetitionParticipants,
  listAllMatchRegistrations,
  updateMatch,
  updateMatchRegistration
} from "../../lib/leaderboardApi";
import { buildAuthReturnPath, buildCurrentReturnTo } from "../../lib/authReturn";
import { getCompetitionAuthProfile } from "../../lib/competitionAuthProfile";
import {
  buildNraArcReport,
  exportNraArcReportFile,
  formatNraArcExportSummary
} from "../../lib/nraArcReportExport";
import { formatCalendarDateTime } from "../../utils/date";
import { CompetitionAuthGate, CompetitionErrorState } from "../Competition/CompetitionRoutePanels";
import "./Leaderboard.css";
import ParticipantOutcomeAudit from "./ParticipantOutcomeAudit";
import RegistrationOrigin from "./RegistrationOrigin";

const STATUS_FILTERS = ["pending", "confirmed", "withdrawn", "cancelled", "all"];
const BANNER_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/webp"]);
const MAX_BANNER_IMAGE_BYTES = 5 * 1024 * 1024;
const ARC_OUTCOME_REASONS = {
  dq: [
    ["arc_dq_unsafe_firearm_handling", "Unsafe firearm handling"],
    ["arc_dq_dropped_loaded_firearm", "Dropped loaded firearm"],
    ["arc_dq_unintentional_discharge", "Unintentional discharge"],
    ["arc_dq_muzzle_safety", "Muzzle safety violation"],
    ["arc_dq_failure_to_stop", "Failure to stop"],
    ["arc_dq_repeated_finger_warning", "Repeated finger warning"],
    ["arc_dq_unsportsmanlike_conduct", "Unsportsmanlike conduct"],
    ["arc_dq_equipment_violation", "Equipment violation"],
    ["arc_dq_match_director_decision", "Match director decision"]
  ],
  dnf: [["arc_dnf_did_not_fire_all_stages", "Did not fire all stages"]],
  withdrawn: [["arc_withdrawn", "Withdrawn"]]
};

function formatDateTime(value) {
  return formatCalendarDateTime(value);
}

function parseAffiliations(value) {
  const seen = new Set();
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter((item) => {
      if (!item) {
        return false;
      }
      const key = item.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
}

function parseCategories(value) {
  return parseAffiliations(value);
}

function formatCategories(value) {
  return Array.isArray(value) && value.length > 0 ? value.join(", ") : "";
}

function formatAffiliations(value) {
  return Array.isArray(value) && value.length > 0 ? value.join(", ") : "-";
}

function hasText(value) {
  return String(value || "").trim().length > 0;
}

function formatStatusLabel(value) {
  const text = String(value || "unknown").trim() || "unknown";
  return text.replace(/_/g, " ");
}

function normalizeMatchSquads(match) {
  const squads = Array.isArray(match?.squads) ? match.squads : [];
  return squads
    .map((squad) => {
      const squadId = String(squad?.id || squad?.squadId || "").trim();
      const rawNumber = squad?.number ?? squad?.squadNumber;
      const squadNumber = Number.isInteger(rawNumber) ? rawNumber : Number.parseInt(String(rawNumber || "").trim(), 10);
      const squadScheduledAt = String(
        squad?.matchTime || squad?.scheduledAt || squad?.squadScheduledAt || ""
      ).trim();

      if (!squadId && !Number.isInteger(squadNumber)) {
        return null;
      }

      return {
        squadId: squadId || "",
        label: String(squad?.label || "").trim(),
        squadNumber: Number.isInteger(squadNumber) ? squadNumber : null,
        squadScheduledAt: squadScheduledAt || ""
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const av = Number.isInteger(a?.squadNumber) ? a.squadNumber : Number.POSITIVE_INFINITY;
      const bv = Number.isInteger(b?.squadNumber) ? b.squadNumber : Number.POSITIVE_INFINITY;
      if (av !== bv) {
        return av - bv;
      }
      return String(a?.squadId || "").localeCompare(String(b?.squadId || ""));
    });
}

function squadChoiceValue(squad) {
  if (!squad) {
    return "";
  }
  if (String(squad.squadId || "").trim()) {
    return `id:${String(squad.squadId).trim()}`;
  }
  if (Number.isInteger(squad.squadNumber)) {
    return `number:${squad.squadNumber}`;
  }
  return "";
}

function findSquadByChoice(squads, choice) {
  const target = String(choice || "").trim();
  if (!target) {
    return null;
  }
  return squads.find((squad) => squadChoiceValue(squad) === target) || null;
}

function findSquadForRegistration(registration, squads) {
  const squadId = String(registration?.squadId || "").trim();
  if (squadId) {
    const byId = squads.find((squad) => String(squad?.squadId || "").trim() === squadId);
    if (byId) {
      return byId;
    }

    return {
      squadId,
      squadNumber: null,
      squadScheduledAt: String(registration?.squadScheduledAt || "").trim()
    };
  }

  const rawNumber = registration?.squadNumber;
  const squadNumber = Number.isInteger(rawNumber) ? rawNumber : Number.parseInt(String(rawNumber || "").trim(), 10);
  if (Number.isInteger(squadNumber)) {
    const byNumber = squads.find((squad) => squad?.squadNumber === squadNumber);
    if (byNumber) {
      return byNumber;
    }

    return {
      squadId,
      squadNumber,
      squadScheduledAt: String(registration?.squadScheduledAt || "").trim()
    };
  }

  return null;
}

function formatSquadLabel(squad) {
  if (!squad) {
    return "No squad assigned";
  }

  const parts = [];
  if (squad.label) {
    parts.push(squad.label);
  } else if (Number.isInteger(squad?.squadNumber)) {
    parts.push(`Squad ${squad.squadNumber}`);
  } else if (String(squad?.squadId || "").trim()) {
    parts.push(`Squad ${String(squad.squadId).trim()}`);
  } else {
    parts.push("Squad");
  }

  if (String(squad?.squadScheduledAt || "").trim()) {
    parts.push(formatDateTime(squad.squadScheduledAt));
  }

  return parts.join(" • ");
}

function buildSquadPayload(squad, { clearWhenMissing = false } = {}) {
  if (!squad) {
    return clearWhenMissing
      ? { squadId: null, squadNumber: null, squadScheduledAt: null }
      : {};
  }

  return {
    ...(String(squad.squadId || "").trim() ? { squadId: String(squad.squadId).trim() } : {}),
    ...(Number.isInteger(squad.squadNumber) ? { squadNumber: squad.squadNumber } : {}),
    ...(String(squad.squadScheduledAt || "").trim() ? { squadScheduledAt: String(squad.squadScheduledAt).trim() } : {})
  };
}

function sortRegistrations(rows) {
  return [...rows].sort((a, b) => {
    const at = String(b?.updatedAt || b?.createdAt || "");
    const bt = String(a?.updatedAt || a?.createdAt || "");
    return at.localeCompare(bt);
  });
}

function buildRegistrationProfileDraft(registration) {
  return {
    displayName: String(registration?.displayName || ""),
    email: String(registration?.email || ""),
    divisionId: String(registration?.divisionId || ""),
    classification: String(registration?.classification || ""),
    nraMemberId: String(registration?.nraMemberId || ""),
    nraPrimaryMemberId: String(registration?.nraPrimaryMemberId || ""),
    categoriesText: formatCategories(registration?.categories),
    teamName: String(registration?.teamName || ""),
    affiliationsText: formatCategories(registration?.affiliations),
    isNraAmbassador: registration?.isNraAmbassador === true
  };
}

function normalizeRegistrationProfileDraft(draft) {
  return {
    displayName: String(draft?.displayName || "").trim(),
    email: String(draft?.email || "").trim(),
    divisionId: String(draft?.divisionId || "").trim(),
    classification: String(draft?.classification || "").trim(),
    nraMemberId: String(draft?.nraMemberId || "").trim(),
    nraPrimaryMemberId: String(draft?.nraPrimaryMemberId || "").trim(),
    categories: parseCategories(draft?.categoriesText),
    teamName: String(draft?.teamName || "").trim(),
    affiliations: parseAffiliations(draft?.affiliationsText),
    isNraAmbassador: draft?.isNraAmbassador === true
  };
}

function registrationProfileChanged(registration, draft) {
  const normalized = normalizeRegistrationProfileDraft(draft);
  return (
    normalized.displayName !== String(registration?.displayName || "").trim() ||
    normalized.email !== String(registration?.email || "").trim() ||
    normalized.divisionId !== String(registration?.divisionId || "").trim() ||
    normalized.classification !== String(registration?.classification || "").trim() ||
    normalized.nraMemberId !== String(registration?.nraMemberId || "").trim() ||
    normalized.nraPrimaryMemberId !== String(registration?.nraPrimaryMemberId || "").trim() ||
    normalized.teamName !== String(registration?.teamName || "").trim() ||
    normalized.isNraAmbassador !== (registration?.isNraAmbassador === true) ||
    JSON.stringify(normalized.categories) !== JSON.stringify(Array.isArray(registration?.categories) ? registration.categories : []) ||
    JSON.stringify(normalized.affiliations) !== JSON.stringify(Array.isArray(registration?.affiliations) ? registration.affiliations : [])
  );
}

function OwnedMatchRegistrations() {
  const location = useLocation();
  const { matchId } = useParams();
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [match, setMatch] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [draftSquadChoiceById, setDraftSquadChoiceById] = useState({});
  const [draftProfileById, setDraftProfileById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [bannerBusy, setBannerBusy] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerError, setBannerError] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportError, setExportError] = useState("");
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [newParticipant, setNewParticipant] = useState({
    displayName: "",
    email: "",
    divisionId: "",
    classification: "",
    nraMemberId: "",
    nraPrimaryMemberId: "",
    categoriesText: "",
    teamName: "",
    affiliationsText: "",
    isNraAmbassador: false,
    squadChoice: ""
  });

  const availableSquads = useMemo(() => normalizeMatchSquads(match), [match]);
  const hasConfiguredSquads = availableSquads.length > 0;

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      setActionMessage("");
      setBannerMessage("");
      setBannerError("");

      try {
        const authProfile = await getCompetitionAuthProfile();
        const resolvedUserId = String(authProfile?.userId || "").trim();

        if (!resolvedUserId) {
          throw new Error("Missing user identity");
        }

        const [matchValue, registrationRows, participantRows] = await Promise.all([
          getMatch(matchId, { auth: true }),
          listAllMatchRegistrations(matchId),
          listCompetitionParticipants(matchId)
        ]);

        if (!active) {
          return;
        }

        setOwnerProfile({
          userId: resolvedUserId,
          email: String(authProfile?.email || "").trim(),
          displayName: String(authProfile?.displayName || "").trim()
        });
        setMatch(matchValue);
        const participantsById = new Map(
          (Array.isArray(participantRows) ? participantRows : [])
            .map((participant) => [participant.participantId, participant])
        );
        setRegistrations(sortRegistrations((registrationRows || []).map((registration) => {
          const participant = participantsById.get(registration.participantId);
          return {
            ...registration,
            outcomeStatus: participant?.outcomeStatus || "active",
            outcomeReasonCode: participant?.outcomeReasonCode || "",
            outcomeReasonLabel: participant?.outcomeReasonLabel || "",
            outcomeNotes: participant?.outcomeNotes || "",
            outcomeId: participant?.outcomeId || "",
            outcomeDecisionId: participant?.outcomeDecisionId || "",
            isRankEligible: participant?.isRankEligible !== false
          };
        })));
      } catch (err) {
        if (!active) {
          return;
        }

        const message = String(err?.message || "");
        if (err?.status === 403) {
          setError("This account does not have permission to manage this match.");
        } else if (err?.status === 401 || (!err?.status && (
          message.toLowerCase().includes("sign in") || message.toLowerCase().includes("authenticated")
        ))) {
          setError("Sign in to manage your match registrations.");
        } else {
          setError(message || "Failed to load registrations");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [matchId]);

  useEffect(() => {
    const nextDrafts = {};
    registrations.forEach((registration) => {
      const currentChoice = squadChoiceValue(findSquadForRegistration(registration, availableSquads));
      nextDrafts[registration.competitorId] = currentChoice || (hasConfiguredSquads ? squadChoiceValue(availableSquads[0]) : "");
    });
    setDraftSquadChoiceById(nextDrafts);
  }, [registrations, availableSquads, hasConfiguredSquads]);

  useEffect(() => {
    const nextDrafts = {};
    registrations.forEach((registration) => {
      nextDrafts[registration.competitorId] = buildRegistrationProfileDraft(registration);
    });
    setDraftProfileById(nextDrafts);
  }, [registrations]);

  useEffect(() => {
    setNewParticipant((prev) => {
      if (hasConfiguredSquads) {
        const fallbackChoice = prev.squadChoice || squadChoiceValue(availableSquads[0]);
        return { ...prev, squadChoice: fallbackChoice || "" };
      }

      if (!prev.squadChoice) {
        return prev;
      }

      return { ...prev, squadChoice: "" };
    });
  }, [availableSquads, hasConfiguredSquads]);

  function updateRegistrationRow(nextRow) {
    // Outcome fields are joined from Participant, not returned by registration PATCH.
    setRegistrations((prev) =>
      sortRegistrations(prev.map((row) => (row.registrationId === nextRow.registrationId ? { ...row, ...nextRow } : row)))
    );
  }

  function insertRegistrationRow(nextRow) {
    setRegistrations((prev) => sortRegistrations([nextRow, ...prev.filter((row) => row.competitorId !== nextRow.competitorId)]));
  }

  async function handleBannerUpload(event) {
    const file = event.target.files?.[0] || null;
    event.target.value = "";

    if (!file) {
      return;
    }

    setBannerMessage("");
    setBannerError("");

    if (!BANNER_IMAGE_TYPES.has(file.type)) {
      setBannerError("Upload a PNG, JPG, or WebP banner image.");
      return;
    }

    if (file.size > MAX_BANNER_IMAGE_BYTES) {
      setBannerError("Use a banner image under 5 MB.");
      return;
    }

    setBannerBusy(true);

    try {
      const upload = await createLeaderboardBannerUploadUrl(matchId, {
        contentType: file.type
      });
      const uploadResponse = await fetch(upload.uploadUrl, {
        method: upload.method || "PUT",
        headers: upload.headers || { "content-type": file.type },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed (${uploadResponse.status})`);
      }

      const updatedMatch = await updateMatch(matchId, {
        leaderboardBannerS3Key: upload.s3Key,
        leaderboardBannerContentType: file.type,
        leaderboardBannerUpdatedAt: new Date().toISOString()
      });

      setMatch(updatedMatch);
      setBannerMessage("Leaderboard banner updated.");
    } catch (err) {
      setBannerError(err?.message || "Failed to upload leaderboard banner.");
    } finally {
      setBannerBusy(false);
    }
  }

  async function handleApprove(registration) {
    const busyKey = `approve:${registration.competitorId}`;
    setActionBusyKey(busyKey);
    setActionMessage("");
    setError("");

    try {
      const selectedSquad = findSquadByChoice(availableSquads, draftSquadChoiceById[registration.competitorId]);
      const updated = await confirmMatchRegistration(
        matchId,
        registration.registrationId,
        { ...buildSquadPayload(selectedSquad), expectedUpdatedAt: registration.updatedAt }
      );
      updateRegistrationRow(updated);
      setActionMessage(`Approved ${updated.displayName || "registration"}.`);
    } catch (err) {
      setError(err?.message || "Failed to approve registration");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleSaveSquad(registration) {
    const busyKey = `save:${registration.competitorId}`;
    setActionBusyKey(busyKey);
    setActionMessage("");
    setError("");

    try {
      const selectedSquad = findSquadByChoice(availableSquads, draftSquadChoiceById[registration.competitorId]);
      const updated = await updateMatchRegistration(
        matchId,
        registration.registrationId,
        { ...buildSquadPayload(selectedSquad, { clearWhenMissing: true }), expectedUpdatedAt: registration.updatedAt }
      );
      updateRegistrationRow(updated);
      setActionMessage(`Updated squad for ${updated.displayName || "registration"}.`);
    } catch (err) {
      setError(err?.message || "Failed to update squad");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleSaveProfile(registration) {
    const busyKey = `profile:${registration.competitorId}`;
    const draft = draftProfileById[registration.competitorId] || buildRegistrationProfileDraft(registration);
    const normalized = normalizeRegistrationProfileDraft(draft);

    if (!normalized.displayName) {
      setError("Participant name is required.");
      return;
    }

    setActionBusyKey(busyKey);
    setActionMessage("");
    setError("");

    try {
      const updated = await updateMatchRegistration(matchId, registration.registrationId, {
        expectedUpdatedAt: registration.updatedAt,
        displayName: normalized.displayName,
        email: normalized.email || null,
        divisionId: normalized.divisionId || null,
        classification: normalized.classification || null,
        nraMemberId: normalized.nraMemberId || null,
        nraPrimaryMemberId: normalized.nraPrimaryMemberId || null,
        categories: normalized.categories,
        teamName: normalized.teamName || null,
        affiliations: normalized.affiliations,
        isNraAmbassador: normalized.isNraAmbassador
      });
      updateRegistrationRow(updated);
      setActionMessage(`Updated NRA details for ${updated.displayName || "registration"}.`);
    } catch (err) {
      setError(err?.message || "Failed to update competitor details");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleCreateParticipant() {
    if (actionBusyKey) {
      return;
    }

    const trimmedName = String(newParticipant.displayName || "").trim();
    if (!trimmedName) {
      setError("Participant name is required.");
      return;
    }

    const selectedSquad = findSquadByChoice(availableSquads, newParticipant.squadChoice);
    if (hasConfiguredSquads && !selectedSquad) {
      setError("Choose a squad before adding a participant.");
      return;
    }

    setActionBusyKey("create-participant");
    setActionMessage("");
    setError("");

    try {
      const created = await createOwnerManagedMatchRegistration(matchId, {
        displayName: trimmedName,
        email: String(newParticipant.email || "").trim() || undefined,
        divisionId: String(newParticipant.divisionId || "").trim() || undefined,
        classification: String(newParticipant.classification || "").trim() || undefined,
        nraMemberId: String(newParticipant.nraMemberId || "").trim() || undefined,
        nraPrimaryMemberId: String(newParticipant.nraPrimaryMemberId || "").trim() || undefined,
        categories: parseCategories(newParticipant.categoriesText),
        teamName: String(newParticipant.teamName || "").trim() || undefined,
        affiliations: parseAffiliations(newParticipant.affiliationsText),
        isNraAmbassador: newParticipant.isNraAmbassador === true,
        ...buildSquadPayload(selectedSquad)
      });

      insertRegistrationRow(created);
      setNewParticipant({
        displayName: "",
        email: "",
        divisionId: "",
        classification: "",
        nraMemberId: "",
        nraPrimaryMemberId: "",
        categoriesText: "",
        teamName: "",
        affiliationsText: "",
        isNraAmbassador: false,
        squadChoice: hasConfiguredSquads ? squadChoiceValue(selectedSquad || availableSquads[0]) : ""
      });
      setActionMessage(`Added ${created.displayName || "participant"} to this match.`);
    } catch (err) {
      setError(err?.message || "Failed to add participant");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleExportNraJson() {
    if (exportBusy) {
      return;
    }

    setExportBusy(true);
    setExportMessage("");
    setExportError("");

    try {
      const leaderboardRows = await listMatchLeaderboards({ matchId });
      const report = buildNraArcReport({
        match,
        leaderboardRows,
        registrations
      });
      exportNraArcReportFile(report);
      setExportMessage(formatNraArcExportSummary(report.stats));
    } catch (err) {
      setExportError(err?.message || "Failed to export NRA JSON.");
    } finally {
      setExportBusy(false);
    }
  }

  async function handleSetOutcome(registration, outcomeStatus) {
    const participantId = String(registration?.participantId || "").trim();
    if (!participantId) {
      setError("This registration is missing a canonical participant ID.");
      return;
    }
    const reason = ARC_OUTCOME_REASONS[outcomeStatus]?.[outcomeStatus === "dq" ? 8 : 0];
    if (!reason) {
      setError("Outcome reason is not configured.");
      return;
    }

    const busyKey = `outcome:${registration.competitorId}:${outcomeStatus}`;
    setActionBusyKey(busyKey);
    setError("");
    setActionMessage("");
    try {
      const outcome = await createCompetitionOutcome(matchId, participantId, {
        outcomeStatus,
        reasonCode: reason[0],
        reasonLabel: reason[1]
      });
      setRegistrations((prev) => sortRegistrations(prev.map((row) => row.competitorId === registration.competitorId
        ? {
          ...row,
          outcomeId: outcome.outcomeId,
          outcomeStatus: outcome.outcomeStatus,
          outcomeReasonCode: outcome.reasonCode,
          outcomeReasonLabel: outcome.reasonLabel,
          outcomeNotes: outcome.notes || "",
          isRankEligible: false
        }
        : row
      )));
      setActionMessage(`${registration.displayName || "Competitor"} marked ${String(outcomeStatus).toUpperCase()}.`);
    } catch (err) {
      setError(err?.message || "Failed to update participant outcome");
    } finally {
      setActionBusyKey("");
    }
  }

  async function handleClearOutcome(registration) {
    const participantId = String(registration?.participantId || "").trim();
    const outcomeId = String(registration?.outcomeId || "").trim();
    if (!participantId || !outcomeId) {
      setError("This registration is missing an active outcome ID.");
      return;
    }

    const busyKey = `outcome:${registration.competitorId}:clear`;
    setActionBusyKey(busyKey);
    setError("");
    setActionMessage("");
    try {
      await clearCompetitionOutcome(matchId, participantId, outcomeId);
      setRegistrations((prev) => sortRegistrations(prev.map((row) => row.competitorId === registration.competitorId
        ? {
          ...row,
          outcomeId: "",
          outcomeStatus: "active",
          outcomeReasonCode: "",
          outcomeReasonLabel: "",
          outcomeNotes: "",
          isRankEligible: true
        }
        : row
      )));
      setActionMessage(`${registration.displayName || "Competitor"} outcome cleared.`);
    } catch (err) {
      setError(err?.message || "Failed to clear participant outcome");
    } finally {
      setActionBusyKey("");
    }
  }

  const filteredRegistrations = useMemo(() => {
    if (statusFilter === "all") {
      return registrations;
    }
    return registrations.filter((registration) => String(registration?.status || "").trim() === statusFilter);
  }, [registrations, statusFilter]);

  const stats = useMemo(() => {
    const pending = registrations.filter((registration) => registration.status === "pending").length;
    const confirmed = registrations.filter((registration) => registration.status === "confirmed").length;
    const withdrawn = registrations.filter((registration) => registration.status === "withdrawn").length;
    const cancelled = registrations.filter((registration) => registration.status === "cancelled").length;
    return { pending, confirmed, withdrawn, cancelled, total: registrations.length };
  }, [registrations]);
  const activeRosterFilterLabels = statusFilter === "all" ? [] : [formatStatusLabel(statusFilter)];
  const authGate = error === "Sign in to manage your match registrations.";
  const signInPath = buildAuthReturnPath(
    "/auth/sign-in",
    buildCurrentReturnTo(location, `/matches/manage/arc/${encodeURIComponent(matchId)}`)
  );

  if (!match && (loading || (error && !authGate))) {
    return (
      <section className="leaderboard-page">
        <Helmet><title>Registration Workbench | Range Pocket Competition</title></Helmet>
        {loading ? (
          <div className="leaderboard-card list-empty-panel status-loading-panel" role="status">
            <div className="owner-registration-section-label">Loading</div>
            <strong>Registrations</strong>
          </div>
        ) : (
          <CompetitionErrorState
            eyebrow="Registration workbench"
            title="Registrations could not load"
            message={error}
            onRetry={() => window.location.reload()}
            links={[{ to: "/matches/manage/matches", label: "Owned Matches" }]}
          />
        )}
      </section>
    );
  }

  return (
    <section className="leaderboard-page">
      <Helmet>
        <title>Registration Workbench | Range Pocket Competition</title>
      </Helmet>

      {!authGate && (
        <div className="match-board-hero">
          <div>
            <Link to="/matches/manage/matches?family=arc" className="back-link">
              Owned Matches
            </Link>
            <div className="owner-registration-section-label">NRA ARC</div>
            <h1>{match?.matchName || "Manage Registrations"}</h1>
            <div className="leaderboard-meta">
              {ownerProfile?.displayName || ownerProfile?.email || ownerProfile?.userId || "Director workbench"}
            </div>
          </div>
          <div className="leaderboard-pill-row">
            <span className="open-match-chip">Start {formatDateTime(match?.startDate)}</span>
            <span className="open-match-chip">End {formatDateTime(match?.endDate)}</span>
          </div>
        </div>
      )}

      {authGate && (
        <CompetitionAuthGate
          eyebrow="Director access"
          title="Sign in to manage this match"
          signInPath={signInPath}
          links={[{ to: "/matches/manage/matches", label: "Owned Matches" }]}
        >
          Director registration tools are available after sign-in for the account that owns this match.
        </CompetitionAuthGate>
      )}

      {!authGate && (
        <div className="leaderboard-card director-registration-summary-row">
          <span><strong>{stats.total}</strong> Total</span>
          <span><strong>{stats.pending}</strong> Pending</span>
          <span><strong>{stats.confirmed}</strong> Confirmed</span>
          <span><strong>{stats.withdrawn}</strong> Withdrawn</span>
          <span><strong>{stats.cancelled}</strong> Cancelled</span>
        </div>
      )}

      {!authGate && (
      <section className="leaderboard-card director-registration-workbench">
        <div className="director-registration-workbench-copy">
          <div>
            <div className="owner-registration-section-label">Director Workbench</div>
            <h2>Roster controls</h2>
          </div>
          {match?.leaderboardBannerUrl && (
            <img className="director-registration-banner-thumb" src={match.leaderboardBannerUrl} alt="Current leaderboard banner" />
          )}
        </div>
        <div className="director-registration-toolbar">
          {match?.ownerUserId === ownerProfile?.userId && match?.ownerUserId && <Link
            to={`/matches/manage/arc/${encodeURIComponent(matchId)}/audit`}
            className="registration-auth-secondary leaderboard-inline-link"
          >Action Audit</Link>}
          <Link
            to={`/matches/arc/${encodeURIComponent(matchId)}`}
            className="registration-auth-secondary leaderboard-inline-link"
            aria-label={`Open public page for ${match?.matchName || "this ARC match"}`}
          >
            Public Page
          </Link>
          <Link
            to={`/matches/arc/${encodeURIComponent(matchId)}/display`}
            className="registration-auth-secondary leaderboard-inline-link"
            aria-label={`Launch standings display for ${match?.matchName || "this ARC match"}`}
          >
            Launch display
          </Link>
          <button
            type="button"
            className="match-search-button"
            onClick={handleExportNraJson}
            disabled={exportBusy || loading || !!error}
          >
            {exportBusy ? "Preparing..." : "Export NRA JSON"}
          </button>
          <label className={`registration-auth-secondary director-file-button ${bannerBusy ? "disabled" : ""}`}>
            <input
              className="owner-banner-file-input"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleBannerUpload}
              disabled={bannerBusy}
            />
            {bannerBusy ? "Uploading..." : "Upload Banner"}
          </label>
          <button
            type="button"
            className="match-search-button"
            onClick={() => setAddParticipantOpen(true)}
          >
            Add Participant
          </button>
        </div>
        {(exportMessage || exportError || bannerMessage || bannerError) && (
          <div className="director-registration-messages">
            {exportMessage && <div className="owner-banner-message owner-banner-success">{exportMessage}</div>}
            {exportError && <div className="owner-banner-message owner-banner-error">{exportError}</div>}
            {bannerMessage && <div className="owner-banner-message owner-banner-success">{bannerMessage}</div>}
            {bannerError && <div className="owner-banner-message owner-banner-error">{bannerError}</div>}
          </div>
        )}
      </section>
      )}

      {!authGate && match?.ownerUserId && match.ownerUserId === ownerProfile?.userId &&
        <ParticipantOutcomeAudit key={matchId} matchId={matchId} />}

      {!authGate && (
      <div className="leaderboard-card owner-registration-list-toolbar director-roster-controls">
        <div>
          <div className="owner-registration-section-label">Roster</div>
          <strong>{filteredRegistrations.length} shown</strong>
        </div>
        <label className="match-filter-field owner-registration-status-filter">
          <span>Status</span>
          <select className="match-sort" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>{status === "all" ? "All statuses" : status}</option>
            ))}
          </select>
        </label>
        {statusFilter !== "all" && (
          <button
            type="button"
            className="registration-auth-secondary leaderboard-inline-link owner-registration-clear-filter"
            onClick={() => setStatusFilter("all")}
          >
            Reset
          </button>
        )}
        <div className="leaderboard-meta leaderboard-filter-note">
          {hasConfiguredSquads
            ? "Squads enabled"
            : "No squads configured"}
        </div>
      </div>
      )}

      {!authGate && addParticipantOpen && (
      <details
        id="add-participant"
        className="leaderboard-card owner-registration-panel director-add-participant-details"
        open={addParticipantOpen}
        onToggle={(event) => setAddParticipantOpen(event.currentTarget.open)}
      >
        <summary>
          <span>
            <span className="owner-registration-section-label">Registration</span>
            <strong>Add participant</strong>
          </span>
          <span className="registration-auth-secondary">Open form</span>
        </summary>

        <form
          className="director-add-participant-form"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            handleCreateParticipant();
          }}
        >

        <div className="owner-registration-form-body">
          <div className="owner-registration-form-section">
            <div className="owner-registration-form-section-title">Participant</div>
            <div className="owner-registration-create-grid">
              <label className="leaderboard-field owner-registration-name-field">
                <span>Name</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.displayName}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, displayName: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field">
                <span>Email</span>
                <input
                  className="match-search"
                  type="email"
                  value={newParticipant.email}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, email: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field">
                <span>Division</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.divisionId}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, divisionId: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field">
                <span>Class</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.classification}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, classification: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field">
                <span>NRA Competitive #</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.nraMemberId}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, nraMemberId: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field">
                <span>NRA Primary #</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.nraPrimaryMemberId}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, nraPrimaryMemberId: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              {hasConfiguredSquads && (
                <label className="leaderboard-field">
                  <span>Squad</span>
                  <select
                    className="match-sort"
                    value={newParticipant.squadChoice}
                    onChange={(event) => setNewParticipant((prev) => ({ ...prev, squadChoice: event.target.value }))}
                    disabled={actionBusyKey === "create-participant"}
                  >
                    {availableSquads.map((squad) => (
                      <option key={squadChoiceValue(squad)} value={squadChoiceValue(squad)}>
                        {formatSquadLabel(squad)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>

          <div className="owner-registration-form-section">
            <div className="owner-registration-form-section-title">Affiliation</div>
            <div className="owner-registration-create-grid">
              <label className="leaderboard-field">
                <span>Team</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.teamName}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, teamName: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field owner-registration-affiliations-field">
                <span>Additional affiliations</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.affiliationsText}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, affiliationsText: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-field owner-registration-affiliations-field">
                <span>Categories</span>
                <input
                  className="match-search"
                  type="text"
                  value={newParticipant.categoriesText}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, categoriesText: event.target.value }))}
                  autoComplete="off"
                />
              </label>
              <label className="leaderboard-checkbox-label owner-registration-ambassador-toggle">
                <input
                  type="checkbox"
                  checked={newParticipant.isNraAmbassador}
                  onChange={(event) => setNewParticipant((prev) => ({ ...prev, isNraAmbassador: event.target.checked }))}
                />
                <span>NRA Ambassador</span>
              </label>
            </div>
          </div>
        </div>

        <div className="owner-registration-form-actions">
          <button
            type="submit"
            className="match-search-button owner-registration-submit"
            disabled={actionBusyKey === "create-participant"}
          >
            {actionBusyKey === "create-participant" ? "Adding..." : "Add Participant"}
          </button>
        </div>
        </form>
      </details>
      )}

      {actionMessage && <div className="leaderboard-card leaderboard-success-card">{actionMessage}</div>}
      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel">
          <div className="owner-registration-section-label">Loading</div>
          <strong>Registrations</strong>
        </div>
      )}
      {!loading && error && !authGate && (
        <section className="leaderboard-card app-gate-panel director-error-panel">
          <div className="app-gate-eyebrow">Registration workbench</div>
          <h2>Registrations could not load</h2>
          <p>{error}</p>
          <div className="app-gate-actions">
            <button type="button" className="match-search-button" onClick={() => window.location.reload()}>
              Try Again
            </button>
            <Link to="/matches/manage/matches" className="registration-auth-secondary leaderboard-inline-link">
              Owned Matches
            </Link>
            <Link to="/matches/manage" className="registration-auth-secondary leaderboard-inline-link">
              Director Overview
            </Link>
          </div>
        </section>
      )}

      {!authGate && !loading && !error && filteredRegistrations.length === 0 && (
        <div className="leaderboard-card director-empty-state list-empty-panel">
          <div className="owner-registration-section-label">No results</div>
          <strong>{registrations.length === 0 ? "No registrations yet" : "No registrations found"}</strong>
          {registrations.length === 0 ? (
            <div className="empty-filter-summary" aria-label="Roster state">
              <span>Roster: 0</span>
              <span>ARC</span>
            </div>
          ) : (
            <div className="empty-filter-summary" aria-label="Active filters">
              {activeRosterFilterLabels.map((label) => (
                <span key={label}>{label}</span>
              ))}
            </div>
          )}
          {statusFilter !== "all" && (
            <div className="director-action-row">
              <button
                type="button"
                className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                onClick={() => setStatusFilter("all")}
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {!authGate && !loading && !error && filteredRegistrations.length > 0 && (
        <div className="leaderboard-stack">
          {filteredRegistrations.map((registration) => {
            const currentSquad = findSquadForRegistration(registration, availableSquads);
            const busyApprove = actionBusyKey === `approve:${registration.competitorId}`;
            const busySave = actionBusyKey === `save:${registration.competitorId}`;
            const busyProfile = actionBusyKey === `profile:${registration.competitorId}`;
            const canManageSquad = registration.status === "pending" || registration.status === "confirmed";
            const canManageProfile = registration.status === "pending" || registration.status === "confirmed";
            const draftSquadChoice = draftSquadChoiceById[registration.competitorId] || "";
            const draftProfile = draftProfileById[registration.competitorId] || buildRegistrationProfileDraft(registration);
            const currentSquadChoice = squadChoiceValue(currentSquad);
            const hasSquadControls = hasConfiguredSquads || Boolean(currentSquad);
            const squadChoiceDirty = draftSquadChoice !== currentSquadChoice;
            const profileDirty = registrationProfileChanged(registration, draftProfile);
            const affiliationText = formatAffiliations(registration.affiliations);
            const statusText = String(registration.status || "unknown").trim() || "unknown";
            const hasDivision = hasText(registration.divisionId);
            const hasTeam = hasText(registration.teamName);
            const hasEmail = hasText(registration.email);
            const hasAffiliations = Array.isArray(registration.affiliations) && registration.affiliations.length > 0;
            const hasCategories = Array.isArray(registration.categories) && registration.categories.length > 0;
            const hasClassification = hasText(registration.classification);
            const hasNraMemberId = hasText(registration.nraMemberId);
            const hasNraPrimaryMemberId = hasText(registration.nraPrimaryMemberId);
            const hasCompetitorNumber = hasText(registration.competitorNumber);
            const hasHeaderChip = hasCompetitorNumber || hasDivision || hasTeam || registration.isNraAmbassador;
            const outcomeStatus = String(registration.outcomeStatus || "active").trim().toLowerCase();
            const hasOutcome = outcomeStatus && outcomeStatus !== "active";
            const outcomeLabel = outcomeStatus === "dq"
              ? "DQ"
              : outcomeStatus === "dnf"
                ? "DNF"
                : outcomeStatus === "withdrawn"
                  ? "Withdrawn"
                  : "";
            const summaryItems = [
              hasCompetitorNumber ? { label: "Competitor #", value: `#${registration.competitorNumber}` } : null,
              hasEmail ? { label: "Email", value: registration.email } : null,
              hasClassification ? { label: "Class", value: registration.classification } : null,
              hasCategories ? { label: "Categories", value: registration.categories.join(", ") } : null,
              hasNraMemberId ? { label: "NRA Competitive #", value: registration.nraMemberId } : null,
              hasNraPrimaryMemberId ? { label: "NRA Primary #", value: registration.nraPrimaryMemberId } : null,
              hasAffiliations ? { label: "Affiliations", value: affiliationText } : null,
              hasConfiguredSquads || currentSquad ? { label: "Squad", value: formatSquadLabel(currentSquad) } : null
            ].filter(Boolean);

            return (
              <article key={registration.competitorId} className={`leaderboard-card owner-registration-card owner-registration-card-${statusText}`}>
                <div className="owner-registration-card-header">
                  <div className="owner-registration-card-title">
                    <strong>{registration.displayName || "Unnamed competitor"}</strong>
                    <RegistrationOrigin source={registration.source} />
                    {hasHeaderChip && (
                      <div className="owner-registration-card-subtitle">
                        {hasCompetitorNumber && <span>#{registration.competitorNumber}</span>}
                        {hasDivision && <span>{registration.divisionId}</span>}
                        {hasTeam && <span>{registration.teamName}</span>}
                        {registration.isNraAmbassador && <span className="owner-registration-ambassador-chip">NRA Ambassador</span>}
                      </div>
                    )}
                  </div>
                  <span className={`status-pill status-${statusText}`}>
                    {formatStatusLabel(registration.status)}
                  </span>
                  {hasOutcome && (
                    <span className="status-pill status-cancelled">
                      {outcomeLabel}
                    </span>
                  )}
                </div>

                {summaryItems.length > 0 && (
                  <div className="owner-registration-summary-list">
                    {summaryItems.map((item) => (
                      <div className="owner-registration-summary-item" key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))}
                  </div>
                )}

                <div className="owner-registration-summary-list">
                  <div className="owner-registration-summary-item">
                    <span>NRA details</span>
                    <strong>{profileDirty ? "Unsaved changes" : "Synced"}</strong>
                  </div>
                  <div className="owner-registration-summary-item">
                    <span>Standing</span>
                    <strong>
                      {hasOutcome
                        ? `${outcomeLabel}${registration.outcomeReasonLabel ? ` - ${registration.outcomeReasonLabel}` : ""}`
                        : "Rank eligible"}
                    </strong>
                  </div>
                </div>

                {registration.status === "confirmed" && registration.outcomeDecisionId && (
                  <p className="owner-registration-muted">Device-managed outcome. Changes require the originating scoring device or the match-admin device.</p>
                )}
                {registration.status === "confirmed" && !registration.outcomeDecisionId && (
                  <div className="owner-registration-form-actions owner-registration-outcome-actions">
                    <button
                      type="button"
                      className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                      disabled={Boolean(actionBusyKey)}
                      onClick={() => handleSetOutcome(registration, "dq")}
                    >
                      Mark DQ
                    </button>
                    <button
                      type="button"
                      className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                      disabled={Boolean(actionBusyKey)}
                      onClick={() => handleSetOutcome(registration, "dnf")}
                    >
                      Mark DNF
                    </button>
                    <button
                      type="button"
                      className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                      disabled={Boolean(actionBusyKey)}
                      onClick={() => handleSetOutcome(registration, "withdrawn")}
                    >
                      Withdraw
                    </button>
                    {hasOutcome && (
                      <button
                        type="button"
                        className="match-search-button leaderboard-inline-link detail-link-button"
                        disabled={Boolean(actionBusyKey)}
                        onClick={() => handleClearOutcome(registration)}
                      >
                        Clear Outcome
                      </button>
                    )}
                  </div>
                )}

                <details className="owner-registration-edit-details">
                  <summary>
                    <span>Edit registration details</span>
                    <strong>{profileDirty || squadChoiceDirty ? "Unsaved changes" : "Open editor"}</strong>
                  </summary>

                  <div className="owner-registration-create-grid">
                    <label className="leaderboard-field owner-registration-name-field">
                      <span>Name</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.displayName}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, displayName: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field">
                      <span>Email</span>
                      <input
                        className="match-search"
                        type="email"
                        value={draftProfile.email}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, email: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field">
                      <span>Division</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.divisionId}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, divisionId: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field">
                      <span>Class</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.classification}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, classification: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field">
                      <span>NRA Competitive #</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.nraMemberId}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, nraMemberId: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field">
                      <span>NRA Primary #</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.nraPrimaryMemberId}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, nraPrimaryMemberId: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field">
                      <span>Team</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.teamName}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, teamName: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field owner-registration-affiliations-field">
                      <span>Categories</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.categoriesText}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, categoriesText: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-field owner-registration-affiliations-field">
                      <span>Additional affiliations</span>
                      <input
                        className="match-search"
                        type="text"
                        value={draftProfile.affiliationsText}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, affiliationsText: event.target.value }
                          }))
                        }
                        autoComplete="off"
                        disabled={!canManageProfile || busyProfile}
                      />
                    </label>
                    <label className="leaderboard-checkbox-label owner-registration-ambassador-toggle">
                      <input
                        type="checkbox"
                        checked={draftProfile.isNraAmbassador}
                        onChange={(event) =>
                          setDraftProfileById((prev) => ({
                            ...prev,
                            [registration.competitorId]: { ...draftProfile, isNraAmbassador: event.target.checked }
                          }))
                        }
                        disabled={!canManageProfile || busyProfile}
                      />
                      <span>NRA Ambassador</span>
                    </label>
                  </div>

                  <div className="match-action-row">
                    <button
                      type="button"
                      className="match-search-button"
                      onClick={() => handleSaveProfile(registration)}
                      disabled={!canManageProfile || !profileDirty || busyProfile || busyApprove || busySave}
                    >
                      {busyProfile ? "Saving Details..." : "Save NRA Details"}
                    </button>
                  </div>

                  {hasSquadControls ? (
                    <div className="leaderboard-filter-row">
                      <label className="match-filter-field leaderboard-filter-field">
                        <span>Squad</span>
                        <select
                          className="match-sort"
                          value={draftSquadChoice}
                          onChange={(event) =>
                            setDraftSquadChoiceById((prev) => ({
                              ...prev,
                              [registration.competitorId]: event.target.value
                            }))
                          }
                          disabled={!canManageSquad || busyApprove || busySave}
                        >
                          {!hasConfiguredSquads && <option value="">No squad assigned</option>}
                          {availableSquads.map((squad) => (
                            <option key={squadChoiceValue(squad)} value={squadChoiceValue(squad)}>
                              {formatSquadLabel(squad)}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => handleSaveSquad(registration)}
                        disabled={!canManageSquad || !squadChoiceDirty || busyApprove || busySave}
                      >
                        {busySave ? "Saving..." : "Save Squad"}
                      </button>
                      {registration.status === "pending" && (
                        <button
                          type="button"
                          className="match-search-button"
                          onClick={() => handleApprove(registration)}
                          disabled={busyApprove || busySave}
                        >
                          {busyApprove ? "Approving..." : "Approve"}
                        </button>
                      )}
                    </div>
                  ) : registration.status === "pending" ? (
                    <div className="match-action-row">
                      <button
                        type="button"
                        className="match-search-button"
                        onClick={() => handleApprove(registration)}
                        disabled={busyApprove || busySave}
                      >
                        {busyApprove ? "Approving..." : "Approve"}
                      </button>
                    </div>
                  ) : null}
                </details>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export const OwnedMatchRegistrations_Page = {
  path: "/matches/manage/arc/:matchId",
  element: <OwnedMatchRegistrations />
};
